import { useState } from 'react';
import { useTranslation } from '@klv/hooks/useTranslation';
import VimeoPlayer from 'react-player/vimeo';
import { kebabCase } from 'lodash-es';
import { Rive } from '@klv/atoms/rive';
import { generateClassName } from '@klv/shared/utils/functions';
import Badge from '@klv/atoms/badge';
import Modal from '@klv/organisms/modal';
import Buttons from '@klv/molecules/buttons';

import * as styles from './styles/bfcmContentCard.module.scss';

import type { IBfcmContentCard } from './types';

export const BfcmContentCard = ({
  className = '',
  headline = '',
  font,
  subheadline,
  eyebrow,
  textColor = 'charcoal',
  backgroundColor,
  badge,
  buttons,
  mediaType,
  riveAnimation,
  videoUrl,
  desktopImage,
  mobileImage,
  ...props
}: IBfcmContentCard) => {
  const { t } = useTranslation();
  
  const buttonColor = textColor === 'charcoal' ? 'dark' : 'light';
  // Extract rive configuration from WordPress data
  const riveFile = riveAnimation?.riveFile;
  const layout = riveAnimation?.riveData?.layout;
  const textRuns = riveAnimation?.riveData?.textRuns || [];
  const ariaLabel = riveAnimation?.riveData?.ariaLabel;
  
  const [showVideoModal, setShowVideoModal] = useState(false);

  const HeadlineComponent = font === 'sohneSchmal' ? (
    <div className={styles.headline}>{headline}</div>
  ) : (
    <div
      dangerouslySetInnerHTML={{ __html: headline as string }}
      className="h3"
    />
  );

  const handleClick = () => {
    if (buttons && buttons.length ===  1) {
      window.location.href = buttons[0].url ?? '';
    }
  };

  return (
    <div
      className={generateClassName(['bfcmContentCard', className, styles.bfcmContentCard])}
      style={{
        '--color-content-card-background': `var(--color-${kebabCase(backgroundColor)})`,
        '--color-content-card-text': `var(--color-${kebabCase(textColor)})`,
      } as React.CSSProperties}
      {...props}
    >
      {videoUrl && (
        <div className={styles.videoImageContainer}>
          <button className={styles.imageLink} onClick={() => setShowVideoModal(true)}>
            <div className={styles.desktopImage}>
              <img
                alt={desktopImage?.altText ?? mobileImage?.altText ?? ''}
                className={styles.image}
                height={450}
                loading='eager'
                sizes='(max-width: 767px) 100vw, (max-width: 1365px) 50vw, 600px'
                src={desktopImage?.sourceUrl}
                srcSet={desktopImage?.srcSet}
                width={600}
              />
            </div>
          </button>
          <button className={styles.imageLink} onClick={() => setShowVideoModal(true)}>
            <div className={styles.mobileImage}>
              <img
                alt={mobileImage?.altText ?? desktopImage?.altText ?? ''}
                className={styles.image}
                height={450}
                loading='eager'
                sizes='(max-width: 767px) 100vw, (max-width: 1365px) 50vw, 600px'
                src={mobileImage?.sourceUrl}
                srcSet={mobileImage?.srcSet}
                width={600}
              />
            </div>
          </button>
          <Modal
            contentPadding="30"
            close={() => setShowVideoModal(false)}
            show={showVideoModal}
            size="constrained"
            backgroundColor="lightSand"
            isMediaModal
          >
            <VimeoPlayer
              className={styles.video}
              url={videoUrl}
              controls
              playing={showVideoModal}
              loop={false}
              width="100%"
              height="auto"
              config={{ playerOptions:{ texttrack: t('langCodes_recaptchaLanguage') === 'en' ? '' : t('langCodes_recaptchaLanguage') } } }  
            />
          </Modal>
        </div>
      )}
      {desktopImage?.sourceUrl && (
        <div className={styles.imageContainer}>
          <div className={styles.desktopImage}>
            <img
              alt={desktopImage?.altText ?? mobileImage?.altText ?? ''}
              className={styles.image}
              height={450}
              loading='eager'
              sizes='(max-width: 767px) 100vw, (max-width: 1365px) 50vw, 600px'
              src={desktopImage?.sourceUrl}
              srcSet={desktopImage?.srcSet}
              width={600}
            />
          </div>
          <div className={styles.mobileImage}>
            <img
              alt={mobileImage?.altText ?? desktopImage?.altText ?? ''}
              className={styles.image}
              height={450}
              loading='eager'
              sizes='(max-width: 767px) 100vw, (max-width: 1365px) 50vw, 600px'
              src={mobileImage?.sourceUrl}
              srcSet={mobileImage?.srcSet}
              width={600}
            />
          </div>
        </div>
      )}
      {mediaType === 'riveAnimation' && riveFile && (
      <Rive
        riveFile={riveFile}
        aspectRatio={layout?.aspectRatio}
        flexibleLayout={layout?.isFlexible}
        height={layout?.height}
        riveText={textRuns}
        ariaLabelText={ariaLabel}
      />
  
      )}
      <div className={`${styles.contentContainer} ${buttons && buttons.length === 1 ? styles.cursorPointer : styles.cursorDefault}`} 
        onClick={buttons && buttons.length === 1 ? handleClick : undefined}
      >
        {(Boolean(eyebrow) || Boolean(badge?.text)) && (
          <div className={styles.eyebrowContainer}>
            {eyebrow && (
              <div className='eyebrow'>
                {eyebrow}
              </div>
            )}
            {badge?.text && badge?.color && (
              <Badge color={badge.color} text={badge.text} />
            )}
          </div>
        )}
        {headline && HeadlineComponent}
        {subheadline && <div className={styles.subheadline}>{subheadline}</div>}

        <div className={styles.buttonContainer}>
          {buttons && buttons.length > 0 && (
            <Buttons
              className={styles.button}
              buttons={buttons}
              buttonColor={buttonColor}
              variants={buttons.length === 1 ? ['secondary'] : ['primary', 'secondary']}
            />
          )}
        </div>
      </div>
    </div>
  );
};


